import type { AuthUserDto } from '@/core/data/auth/auth.interface';
import type { LoginMethodType } from '@/core/data/auth/auth.type';
import { type BaseClubDto } from '@/core/data/club/club.interface';
import type { CityDto, GeoCoords } from '@/core/data/location/location.interface';
import type { BaseUserInfo, NotificationPreferences } from '@/core/data/user/user.interface';
import type { PlayerType } from '@/core/data/user/user.type';
import { getPushNotificationToken, isPushPermissionGranted } from '@/core/domain/notification/pushNotifications';
import { gambits } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { FirebaseEndpoint } from '@/core/network/api/constants/api.url.constant';
import { auth } from '@/core/network/firebase';
import { UnitSystem } from '@/views/components/gam-player/helpers';
import { NotificationTopics } from '@/views/composables/constants/gam.constants';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import * as Sentry from '@sentry/vue';
import { signInWithCustomToken, type User } from 'firebase/auth';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useGeolocationStore } from './geolocation.store';

const logger = createLogger('AuthStore');

function prefersImperial(locale: string) {
  const imperialRegions = ['US', 'LR', 'MM'];
  const region = locale.split('-')[1]?.toUpperCase();
  return imperialRegions.includes(region);
}

function getUserPreferredUnits() {
  const locale = navigator.language; // Gets the primary language set in the user's browser (e.g., "en-US", "en-GB", "de-DE")

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: 'meter',
      unitDisplay: 'short',
    });

    const resolvedOptions = formatter.resolvedOptions();
    const resolvedUnit = resolvedOptions.unit;

    if (resolvedUnit === 'mile' || resolvedUnit === 'foot' || resolvedUnit === 'inch') {
      return UnitSystem.IMPERIAL;
    } else if (
      resolvedUnit === 'kilometer' ||
      resolvedUnit === 'meter' ||
      resolvedUnit === 'centimeter' ||
      resolvedUnit === 'millimeter'
    ) {
      return UnitSystem.METRIC;
    } else {
      const region = locale.split('-')[1];
      if (region === 'US' || region === 'LR' || region === 'MM') {
        return UnitSystem.IMPERIAL;
      }
      return UnitSystem.METRIC;
    }
    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
  } catch (error) {
    // Fallback to a default or make a guess based on navigator.language
    const locale = navigator.language || navigator.languages[0];

    if (prefersImperial(locale)) {
      return UnitSystem.IMPERIAL;
    }
    return UnitSystem.METRIC;
  }
}

export const useAuthStore = defineStore(GamStoreId.AUTH, () => {
  const isUpdating = ref<boolean>(false);
  const user = ref<AuthUserDto | null>(null);
  const userDetails = ref<BaseUserInfo | null>(null);
  /** Mirrors legacy `enabledNotifications` ref: derived from `userDetails` so it stays in sync after fetch/update. */
  const notificationPreferences = computed(
    (): NotificationPreferences | undefined => userDetails.value?.notificationPreferences,
  );
  const userClubs = ref<BaseClubDto[] | null>(null);
  const firebaseUser = ref<User | null>(null);
  const unitType = ref<UnitSystem>(getUserPreferredUnits());

  const redirectAfterAuth = ref<string | null>(null);
  function setRedirectPath(path: string) {
    redirectAfterAuth.value = path;
  }
  function clearRedirectPath() {
    redirectAfterAuth.value = null;
  }

  const connecting = ref<LoginMethodType | null>();
  const setConnecting = (type: LoginMethodType | null) => {
    connecting.value = type;
  };

  const geolocationStore = useGeolocationStore();
  const { isUserLocated, userLocation } = storeToRefs(geolocationStore);

  const isAuthenticated = () => {
    return gambits.authService.isAuthenticated();
  };

  const isActive = () => {
    return gambits.authService.isActive();
  };

  const getUserDetails = () => {
    return userDetails.value;
  };

  const isAdmin = (): boolean => {
    return !!user.value?.isAdmin;
  };

  const setUser = (value: AuthUserDto | null) => {
    user.value = value;
  };

  const setUserCity = (city: CityDto | null) => {
    if (!userDetails.value) return;

    const currentCity = userDetails.value.city;
    const nextCity = city || undefined;
    const shouldMergeSameCity = !!currentCity?.id && !!nextCity?.id && currentCity.id === nextCity.id;

    const mergedCity = shouldMergeSameCity
      ? {
          ...currentCity,
          ...Object.fromEntries(Object.entries(nextCity).filter(([, value]) => value !== undefined)),
        }
      : nextCity;

    userDetails.value = {
      ...userDetails.value,
      city: mergedCity,
    };
  };

  const storePushToken = async () => {
    if (!isActive() || !isPushPermissionGranted()) return;

    const token = await getPushNotificationToken();
    if (typeof token !== 'string' || token.length === 0) {
      return;
    }

    await gambits.userService.storeNotificationToken(token, [NotificationTopics.GAMBIT]);
  };

  const storeLocation = async () => {
    if (!isActive() || !isUserLocated.value || !userLocation.value?.location) return;

    // if (!geolocationStore.shouldUpdateLocationData(userLocation.value)) return;

    await gambits.userService.updateLocation(userLocation.value);
  };

  const logoutUser = async () => {
    setUser(null);
    userDetails.value = null;
    firebaseUser.value = null;
    Sentry.setUser(null);
  };

  const fetchUserInfo = async (refresh?: boolean): Promise<void> => {
    if (!isAuthenticated()) return;

    isUpdating.value = refresh === true;
    let result = await gambits.userService.getUserInfo(refresh);
    isUpdating.value = false;

    if (result.error?.code === 404) {
      logger.warn('User info returned 404, retrying once before logout...');
      await new Promise((r) => setTimeout(r, 1500));
      result = await gambits.userService.getUserInfo(true);

      if (result.error?.code === 404) {
        logger.error('User info returned 404 on retry — logging out');
        await gambits.authService.logout();
        return;
      }
    }

    if (!result.data) {
      return;
    }

    userDetails.value = result.data;
    if (result.data.unitType) {
      unitType.value = result.data.unitType;
    } else {
      const detectedUnits = getUserPreferredUnits();
      unitType.value = detectedUnits;
      updateUnitTypePreferences(detectedUnits);
    }
  };

  const fetchUserClubs = async (refresh?: boolean): Promise<void> => {
    if (!isAuthenticated()) return;

    isUpdating.value = !!refresh;

    const userClubsResult = await gambits.userService.getUserClubs(refresh);

    if (userClubsResult?.data) {
      userClubs.value = userClubsResult.data;
    }

    isUpdating.value = false;
  };

  const updateNotificationPreferences = async (
    notificationPreferences: import('@/core/data/user/user.interface').NotificationPreferences,
  ): Promise<boolean> => {
    if (!isAuthenticated()) return false;

    isUpdating.value = true;

    const response = await gambits.userService.updateNotificationPreferences(notificationPreferences);

    isUpdating.value = false;

    if (response?.error || !response?.data) {
      return false;
    }

    // Must bypass session cache — getUserInfo(false) returns stale UserInfo from storage and would
    // overwrite the prefs we just saved (see UserRepository.getUserInfo).
    await fetchUserInfo(true);

    return true;
  };

  const updateUnitTypePreferences = async (units: UnitSystem) => {
    if (!isAuthenticated()) return;

    isUpdating.value = true;

    const response = await gambits.userService.updateUnitTypePreferences(units);

    isUpdating.value = false;

    if (!response?.data) return;

    unitType.value = units;

    if (!userDetails.value) return;

    userDetails.value.unitType = units;
  };

  const updateAccentHue = async (accentHue: number | null) => {
    if (!isAuthenticated()) return;

    if (userDetails.value) {
      userDetails.value.accentHue = accentHue;
    }

    isUpdating.value = true;
    await gambits.userService.updateAccentHue(accentHue);
    isUpdating.value = false;
  };

  const updateRecentClockControls = async (controls: { minutes: number; increment: number }[]) => {
    if (!isAuthenticated()) return;

    if (userDetails.value) {
      userDetails.value.recentClockControls = controls;
    }

    const result = await gambits.userService.updateRecentClockControls(controls);
    if (result.error) {
      await fetchUserInfo(true);
    }
  };

  const updatePlayerTypes = async (playerTypes: PlayerType[]) => {
    if (!isAuthenticated() || !userDetails.value) return;

    isUpdating.value = true;

    const userProfileForm = {
      ...userDetails.value,
      firstName: userDetails.value.firstName as string,
      lastName: userDetails.value.lastName as string,
      unitType: userDetails.value.unitType as UnitSystem,
    };
    const response = await gambits.userService.updatePlayerTypes(
      userProfileForm,
      playerTypes,
      userDetails.value.birthDate!,
      userDetails.value.profileImageBlobHash || undefined,
    );

    isUpdating.value = false;

    if (!response?.data) return;
    if (!userDetails.value) return;

    userDetails.value.playerTypes = playerTypes;
  };

  const updateUserCity = async (coordinates: GeoCoords): Promise<CityDto | null> => {
    if (!isAuthenticated()) return null;

    isUpdating.value = true;
    const response = await gambits.userService.updateCity(coordinates);
    isUpdating.value = false;

    if (!response.data) return null;

    setUserCity(response.data);

    return response.data;
  };

  const authorizeFirebase = async () => {
    const jwt = gambits.jwtService.getTokenInfo();

    if (!jwt?.accessToken) return;

    try {
      const resp = await fetch(`${gambits.configService.getServiceRoutingHost()}${FirebaseEndpoint.AUTH}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt.accessToken}` },
      });

      if (resp.status !== 201) {
        return;
      }

      const token = await resp.text();

      if (typeof token !== 'string' || token.length === 0) {
        return;
      }

      const firebaseResp = await signInWithCustomToken(auth, token);
      firebaseUser.value = firebaseResp.user;
    } catch (e) {
      if (e instanceof Error) {
        logger.error('Failed at Firebase authentication: ' + e.message);
      } else {
        logger.error('Failed at Firebase authentication: Unknown error');
      }
    }
  };

  watch(
    () => [user.value, gambits.jwtService.getTokenInfo()?.accessToken],
    ([user, jwt]) => {
      if (!user) return;

      const isAuthenticated = typeof jwt === 'string' && jwt.length;
      if (!isAuthenticated) return;

      const { id, isActive } = user as AuthUserDto;

      Sentry.setUser({ id });

      if (!isActive) return;

      authorizeFirebase();
      storeLocation();
      storePushToken();
    },
    { immediate: true },
  );

  const initialize = async () => {
    const user = await gambits.authService.getUser();
    if (!user) return;

    setUser(user);
    await fetchUserInfo(true);
  };

  return {
    isUpdating,
    firebaseUser,
    user,
    userDetails,
    notificationPreferences,
    userClubs,
    unitType,
    connecting,
    redirectAfterAuth,
    setRedirectPath,
    clearRedirectPath,
    setUser,
    setUserCity,
    fetchUserInfo,
    fetchUserClubs,
    getUserDetails,
    updateNotificationPreferences,
    updateUnitTypePreferences,
    updateAccentHue,
    updateRecentClockControls,
    updatePlayerTypes,
    updateUserCity,
    storePushToken,
    storeLocation,
    logoutUser,
    isAuthenticated,
    isActive,
    isAdmin,
    setConnecting,
    authorizeFirebase,
    initialize,
  };
});
